import type {Auth, AuthableUser} from 'typings/portaro.be.types';
import type {Observable} from 'rxjs';
import {BehaviorSubject} from 'rxjs';

/**
 * @ngdoc type
 * @name TRole
 * @module portaro.services
 *
 * @description
 * Role name based on backend names.
 */
export type TRole = keyof typeof UserRoles;

export const UserRoles = {
    ROLE_EVIDED: 'ROLE_EVIDED',
    ROLE_READER: 'ROLE_READER',
    ROLE_LIBRARY: 'ROLE_LIBRARY',
    ROLE_SUPPLIER: 'ROLE_SUPPLIER',
    ROLE_LIBRARIAN: 'ROLE_LIBRARIAN',
    ROLE_ADMIN: 'ROLE_ADMIN',
    ROLE_ACTUATOR: 'ROLE_ACTUATOR',
    ROLE_SERVICEMAN: 'ROLE_SERVICEMAN',
    ROLE_EXTERNALLY_AUTHENTICATED: 'ROLE_EXTERNALLY_AUTHENTICATED'
} as const;

/**
 * @ngdoc service
 * @name currentAuthService
 * @module portaro.services
 *
 * @requires provider:currentAuthProvider
 *
 * @description
 * CurrentAuth operations
 */
export default class CurrentAuthService {
    public static serviceName = 'currentAuthService';

    private readonly currentAuthObservable$: BehaviorSubject<Auth>;

    /*@ngInject*/
    constructor(currentAuth: Auth) {
        this.currentAuthObservable$ = new BehaviorSubject<Auth>(currentAuth);
    }

    /**
     * @ngdoc method
     * @name currentAuthService#hasRole
     *
     * @param {TRole} role Tested role.
     * @returns {boolean}
     *
     * @description
     * Has current user specified role?
     */
    public hasRole(role: TRole): boolean {
        return this.currentAuthObservable$.getValue().role.includes(role);
    }

    /**
     * @ngdoc method
     * @name currentAuthService#hasNoRole
     *
     * @param {...TRole} roles Tested roles
     * @returns {boolean}
     *
     * @description
     * Has current user neither of specified roles
     */
    public hasNoRole(...roles: TRole[]): boolean {
        if (roles) {
            return roles.every((role) => !this.hasRole(role));
        }
        return false;
    }

    /**
     * @ngdoc method
     * @name currentAuthService#hasAnyRole
     *
     * @param {...TRole} roles Tested roles
     * @returns {boolean}
     *
     * @description
     * Has current user any of specified roles?
     */
    public hasAnyRole(...roles: TRole[]): boolean {
        if (roles) {
            return roles.some((role) => this.hasRole(role));
        }
        return false;
    }

    /**
     * @ngdoc method
     * @name currentAuthService#isLoggedIn
     *
     * @returns {boolean}
     *
     * @description
     * Is user logged in?
     */
    public isLoggedIn(): boolean {
        return this.currentAuthObservable$.getValue().evided;
    }

    /**
     * @ngdoc method
     * @name currentAuthService#isNotLoggedIn
     *
     * @returns {boolean}
     *
     * @description
     * Is user anonymous?
     */
    public isNotLoggedIn(): boolean {
        return !this.isLoggedIn();
    }

    /**
     * @ngdoc method
     * @name currentAuthService#getActiveUser
     *
     * @returns {AuthableUser}
     *
     * @description
     * gets active user
     */
    public getActiveUser(): AuthableUser {
        return this.currentAuthObservable$.getValue().activeUser;
    }

    public currentAuth$(): Observable<Auth> {
        return this.currentAuthObservable$.asObservable();
    }

    public getCurrentAuthValue(): Auth {
        return this.currentAuthObservable$.getValue();
    }

    public notifyNewAuth(newAuth: Auth): void {
        this.currentAuthObservable$.next(newAuth);
    }
}
